import logger from '../utils/logger';
import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import { settingsService } from '../services/settingsService';
import { useSettings } from './SettingsContext';

const SessionContext = createContext();

export const useSession = () => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
};

export const SessionProvider = ({ children }) => {
  const { logout, isAuthenticated } = useAuth();
  const { sessionSettings, isLoading: settingsLoading } = useSettings();
  const [sessionTimeout, setSessionTimeout] = useState(3600); // Default 1 hour
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [showWarning, setShowWarning] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const timeoutRef = useRef(null);
  const warningTimeoutRef = useRef(null);
  const countdownRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const handleSessionTimeoutRef = useRef(null);

  // Load session timeout from centralized SettingsContext
  useEffect(() => {
    if (!settingsLoading && sessionSettings) {
      if (sessionSettings.session_timeout) {
        setSessionTimeout(sessionSettings.session_timeout);
      }
      setIsLoading(false);
    }
  }, [sessionSettings, settingsLoading]);

  // Reset session timer
  const resetSessionTimer = useCallback(() => {
    if (!isAuthenticated()) return;

    lastActivityRef.current = Date.now();
    setShowWarning(false);
    setTimeRemaining(null);

    // Clear existing timeouts
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
    }

    // Set warning timeout (5 minutes before session expires)
    const warningTime = (sessionTimeout - 300) * 1000; // 5 minutes before timeout
    warningTimeoutRef.current = setTimeout(() => {
      setShowWarning(true);
      setTimeRemaining(300); // 5 minutes remaining

      // Start countdown
      countdownRef.current = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 1) {
            // Session expired
            handleSessionTimeoutRef.current?.();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }, Math.max(warningTime, 0));

    // Set session timeout
    timeoutRef.current = setTimeout(() => {
      handleSessionTimeoutRef.current?.();
    }, sessionTimeout * 1000);
  }, [sessionTimeout, isAuthenticated]);

  // Handle session timeout
  const handleSessionTimeout = useCallback(() => {
    setShowWarning(false);
    setTimeRemaining(null);

    // Clear all timeouts
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
    }

    // Log out user
    logout();
  }, [logout]);

  // Keep ref in sync so timer callbacks always use latest version
  handleSessionTimeoutRef.current = handleSessionTimeout;

  // Extend session
  const extendSession = useCallback(() => {
    resetSessionTimer();
  }, [resetSessionTimer]);

  // Track user activity
  const trackActivity = useCallback(() => {
    if (!isAuthenticated()) return;
    
    const now = Date.now();
    const timeSinceLastActivity = now - lastActivityRef.current;
    
    // Only reset timer if enough time has passed (prevent excessive resets)
    if (timeSinceLastActivity > 60000) { // 1 minute threshold
      resetSessionTimer();
    }
  }, [resetSessionTimer, isAuthenticated]);

  // Set up activity listeners
  useEffect(() => {
    if (!isAuthenticated()) return;

    const activityEvents = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click'
    ];

    // Throttled activity handler
    let activityTimeout;
    const throttledTrackActivity = () => {
      if (activityTimeout) return;
      
      activityTimeout = setTimeout(() => {
        trackActivity();
        activityTimeout = null;
      }, 1000); // Throttle to once per second
    };

    // Add event listeners
    activityEvents.forEach(event => {
      document.addEventListener(event, throttledTrackActivity, { passive: true });
    });

    // Initial timer setup
    resetSessionTimer();

    // Cleanup
    return () => {
      activityEvents.forEach(event => {
        document.removeEventListener(event, throttledTrackActivity);
      });
      
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
      }
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
      }
      if (activityTimeout) {
        clearTimeout(activityTimeout);
      }
    };
  }, [isAuthenticated, resetSessionTimer, trackActivity]);

  // Update session timeout when settings change
  const updateSessionTimeout = useCallback(async (newTimeout) => {
    try {
      setSessionTimeout(newTimeout);
      
      // Save to settings
      await settingsService.updateSettingsBulk('general', {
        session_timeout: newTimeout
      });
      
      // Reset timer with new timeout
      if (isAuthenticated()) {
        resetSessionTimer();
      }
    } catch (error) {
      logger.error('Failed to save session timeout setting:', error);
      // Revert on error
      await loadSessionTimeoutFromSettings();
    }
  }, [isAuthenticated, resetSessionTimer]);

  // Format time remaining for display
  const formatTimeRemaining = useCallback((seconds) => {
    if (!seconds) return '';
    
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    
    if (minutes > 0) {
      return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    }
    return `${remainingSeconds}s`;
  }, []);

  const value = {
    sessionTimeout,
    timeRemaining,
    showWarning,
    isLoading,
    extendSession,
    updateSessionTimeout,
    formatTimeRemaining,
    resetSessionTimer
  };

  return (
    <SessionContext.Provider value={value}>
      {children}
      
      {/* Session Warning Modal */}
      {showWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md mx-4 shadow-xl">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-yellow-100 dark:bg-yellow-900 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                  Session Expiring
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Your session will expire in {formatTimeRemaining(timeRemaining)}
                </p>
              </div>
            </div>
            
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              You will be automatically logged out for security reasons. Click "Stay Logged In" to extend your session.
            </p>
            
            <div className="flex space-x-3">
              <button
                onClick={extendSession}
                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 font-medium"
              >
                Stay Logged In
              </button>
              <button
                onClick={handleSessionTimeout}
                className="flex-1 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-md hover:bg-gray-400 dark:hover:bg-gray-500"
              >
                Log Out Now
              </button>
            </div>
          </div>
        </div>
      )}
    </SessionContext.Provider>
  );
};

export default SessionContext;